// src/features/privateAlbum/components/AlbumList.jsx

import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Typography,
  Chip,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  CircularProgress,
  Stack,
  Paper,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  Visibility as ViewIcon,
  Archive as ArchiveIcon,
  Restore as RestoreIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { formatDate } from "../utils/privateAlbumHelpers";
import { getAlbumStatusDisplay } from "../utils/privateAlbumHelpers";

const AlbumList = ({
  albums = [],
  onSelect,
  onDelete,
  onArchive,
  onRestore,
  filters = {},
  onFilterChange,
  loading = false,
}) => {
  const [searchTerm, setSearchTerm] = useState(filters?.search || "");

  // Debug: Log when albums change
  useEffect(() => {
    console.log("AlbumList received albums:", albums);
    console.log("AlbumList received loading:", loading);
    console.log("AlbumList received filters:", filters);
  }, [albums, loading, filters]);

  useEffect(() => {
    setSearchTerm(filters?.search || "");
  }, [filters?.search]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (onFilterChange) {
      onFilterChange({ search: searchTerm });
    }
  };

  const handleStatusChange = (e) => {
    if (onFilterChange) {
      onFilterChange({ status: e.target.value });
    }
  };

  const handleEventTypeChange = (e) => {
    if (onFilterChange) {
      onFilterChange({ eventType: e.target.value });
    }
  };

  const handleClearFilters = () => {
    setSearchTerm("");
    if (onFilterChange) {
      onFilterChange({ search: "", status: "all", eventType: "all" });
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!albums || albums.length === 0) {
    return (
      <Box sx={{ textAlign: "center", py: 8 }}>
        <Typography variant="h6" color="text.secondary">
          No albums found
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Create your first album to get started
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Filters */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
            alignItems: "center",
          }}
        >
          <form onSubmit={handleSearch} style={{ flex: 1, minWidth: 200 }}>
            <TextField
              fullWidth
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search albums..."
              InputProps={{
                endAdornment: (
                  <IconButton type="submit" size="small">
                    <SearchIcon />
                  </IconButton>
                ),
              }}
            />
          </form>

          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={filters?.status || "all"}
              onChange={handleStatusChange}
              label="Status"
            >
              <MenuItem value="all">All Status</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="archived">Archived</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Event</InputLabel>
            <Select
              value={filters?.eventType || "all"}
              onChange={handleEventTypeChange}
              label="Event"
            >
              <MenuItem value="all">All Events</MenuItem>
              <MenuItem value="wedding">Wedding</MenuItem>
              <MenuItem value="birthday">Birthday</MenuItem>
              <MenuItem value="engagement">Engagement</MenuItem>
              <MenuItem value="maternity">Maternity</MenuItem>
              <MenuItem value="corporate">Corporate</MenuItem>
              <MenuItem value="portrait">Portrait</MenuItem>
              <MenuItem value="event">Event</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </Select>
          </FormControl>

          <Button
            variant="outlined"
            color="inherit"
            onClick={handleClearFilters}
            startIcon={<ClearIcon />}
            size="small"
          >
            Clear Filters
          </Button>
        </Box>
      </Paper>

      {/* Album Grid */}
      <Grid container spacing={3}>
        {albums.map((album) => {
          const statusDisplay = getAlbumStatusDisplay(album.status);
          return (
            <Grid item xs={12} sm={6} md={4} key={album.id}>
              <Card
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  transition: "transform 0.2s, box-shadow 0.2s",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: 6,
                    cursor: "pointer",
                  },
                }}
                onClick={() => onSelect && onSelect(album.id)}
              >
                {/* Cover Image */}
                <Box
                  sx={{
                    position: "relative",
                    pt: "56.25%",
                    bgcolor: "grey.200",
                  }}
                >
                  {album.coverImageUrl ? (
                    <CardMedia
                      component="img"
                      image={album.coverImageUrl}
                      alt={album.title}
                      sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: "grey.300",
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        No Cover
                      </Typography>
                    </Box>
                  )}
                  <Chip
                    label={statusDisplay.label}
                    color={
                      statusDisplay.color === "success"
                        ? "success"
                        : statusDisplay.color === "warning"
                          ? "warning"
                          : "error"
                    }
                    size="small"
                    sx={{
                      position: "absolute",
                      top: 8,
                      right: 8,
                    }}
                  />
                </Box>

                {/* Album Info */}
                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography variant="h6" noWrap>
                    {album.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                      mb: 2,
                    }}
                  >
                    {album.description || "No description"}
                  </Typography>

                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    spacing={1}
                  >
                    <Typography variant="body2" color="text.secondary">
                      {album.clientNames || "No client"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {album.totalPhotos || 0} photos
                    </Typography>
                  </Stack>

                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    spacing={1}
                    sx={{ mt: 1 }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      {album.eventType || "N/A"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {formatDate(album.eventDate)}
                    </Typography>
                  </Stack>

                  {/* Actions */}
                  <Box
                    sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: "divider" }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Stack direction="row" spacing={1}>
                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        fullWidth
                        onClick={() => onSelect && onSelect(album.id)}
                        startIcon={<ViewIcon />}
                      >
                        View
                      </Button>
                      {album.status === "active" ? (
                        <Tooltip title="Archive">
                          <IconButton
                            color="warning"
                            size="small"
                            onClick={() => onArchive && onArchive(album.id)}
                          >
                            <ArchiveIcon />
                          </IconButton>
                        </Tooltip>
                      ) : album.status === "archived" ? (
                        <Tooltip title="Restore">
                          <IconButton
                            color="success"
                            size="small"
                            onClick={() => onRestore && onRestore(album.id)}
                          >
                            <RestoreIcon />
                          </IconButton>
                        </Tooltip>
                      ) : null}
                      <Tooltip title="Delete">
                        <IconButton
                          color="error"
                          size="small"
                          onClick={() => onDelete && onDelete(album.id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

export default AlbumList;
